import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { message, Spin, Modal } from "antd";

import ReusableHeader from "../../../../reusableField/ReusableHeader";
import BackIcon from "../../../../Images/backIcon.png";
import HomeIcon from "../../../../Images/homeIcon.png";
import DeleteIcon from "../../../../Images/deleteIcon2.png";
import editIcon from "../../../../Images/editIcon.png";

import {
  getRoleDetailsList,
  getRoleDetailOptions,
  deleteRoleDetail,
} from "../../../../services/SyncApis";

const RoleDetailsConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const fullState = location.state || {};

  /*
   * =====================================================
   * ROLE / CLIENT CONTEXT
   * =====================================================
   */

  const roleId = fullState.roleId;
  const accountId = fullState.accountId;
  const accountName =
    fullState.accountName || "Admin";

  const safeRoleName =
    fullState.name ||
    "Experience Configuration";

  /*
   * =====================================================
   * STATE
   * =====================================================
   */

  const [roleDetails, setRoleDetails] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [optionsLoading, setOptionsLoading] =
    useState(false);

  const [searchQuery, setSearchQuery] =
    useState("");

  const [currentPage, setCurrentPage] =
    useState(1);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [selectedDeleteJobName, setSelectedDeleteJobName] =
    useState("");
  /*
   * API page size.
   *
   * We are using 5 because your current UI
   * was already designed around 5 cards.
   */
  const pageSize = 5;

  /*
   * =====================================================
   * OPTIONS
   *
   * Used to convert IDs returned by Role Details API
   * into human-readable names.
   * =====================================================
   */

  const [employmentTypes, setEmploymentTypes] =
    useState([]);

  const [workModes, setWorkModes] =
    useState([]);

  const [locations, setLocations] =
    useState([]);

  /*
   * =====================================================
   * SERVER PAGINATION
   * =====================================================
   */

  const [pagination, setPagination] =
    useState({
      page: 1,
      pageSize,
      totalItems: 0,
      totalPages: 1,
    });

  /*
   * =====================================================
   * FETCH ALL ROLE DETAILS
   *
   * GET
   * /api/admin/role-details
   *
   * adminClientId
   * adminRoleId
   * page
   * pageSize
   * =====================================================
   */

  const fetchRoleDetails = async (
    page = currentPage
  ) => {
    if (!roleId || !accountId) {
      return;
    }

    setLoading(true);

    try {
      const response =
        await getRoleDetailsList({
          adminClientId:
            Number(accountId),

          adminRoleId:
            Number(roleId),

          page,

          pageSize,
        });

      /*
       * API response:
       *
       * {
       *   success: true,
       *   items: [],
       *   pagination: {}
       * }
       */

      const items =
        Array.isArray(response?.items)
          ? response.items
          : [];

      setRoleDetails(items);

      setPagination(
        response?.pagination || {
          page,
          pageSize,
          totalItems: items.length,
          totalPages:
            Math.ceil(
              items.length / pageSize
            ) || 1,
        }
      );
    } catch (error) {
      console.error(
        "Error fetching role details:",
        error
      );

      message.error(
        error?.response?.data?.message ||
        "Failed to fetch role details"
      );

      setRoleDetails([]);

      setPagination({
        page: 1,
        pageSize,
        totalItems: 0,
        totalPages: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  /*
   * =====================================================
   * FETCH DROPDOWN OPTIONS
   *
   * Needed because Role Details list returns IDs:
   *
   * employmentTypeId
   * workModeId
   * locations: [1, 2]
   * =====================================================
   */

  const fetchOptions = async () => {
    setOptionsLoading(true);

    try {
      const response =
        await getRoleDetailOptions();

      const data =
        response?.data || {};

      setEmploymentTypes(
        Array.isArray(
          data.employmentTypes
        )
          ? data.employmentTypes
          : []
      );

      setWorkModes(
        Array.isArray(data.workModes)
          ? data.workModes
          : []
      );

      setLocations(
        Array.isArray(data.locations)
          ? data.locations
          : []
      );
    } catch (error) {
      console.error(
        "Error fetching role detail options:",
        error
      );

      message.error(
        "Failed to load role detail options."
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  /*
   * =====================================================
   * INITIAL LOAD
   * =====================================================
   */

  useEffect(() => {
    fetchRoleDetails(1);
    fetchOptions();
  }, [roleId, accountId]);

  /*
   * =====================================================
   * PAGE CHANGE
   * =====================================================
   */

  useEffect(() => {
    if (
      currentPage !== 1 &&
      roleId &&
      accountId
    ) {
      fetchRoleDetails(currentPage);
    }
  }, [currentPage]);

  /*
   * =====================================================
   * LOCALIZED TEXT
   * =====================================================
   */

  const getLocalizedText = (val) => {
    if (
      val === null ||
      val === undefined
    ) {
      return "";
    }

    if (typeof val === "string") {
      return val;
    }

    if (typeof val === "object") {
      if (
        val.en &&
        typeof val.en === "string"
      ) {
        return val.en;
      }

      const firstStr =
        Object.values(val).find(
          (value) =>
            typeof value === "string"
        );

      if (firstStr) {
        return firstStr;
      }

      try {
        return JSON.stringify(val);
      } catch (error) {
        return String(val);
      }
    }

    return String(val);
  };

  /*
   * =====================================================
   * SEARCH
   *
   * There is currently no search query parameter
   * documented for Role Details API.
   *
   * Therefore we filter the currently fetched page.
   * =====================================================
   */

  const filteredRoleDetails =
    roleDetails.filter((item) => {
      if (!searchQuery.trim()) {
        return true;
      }

      const searchText =
        [
          item?.designation,
          item?.companyName,
          item?.roleName,
        ]
          .map(getLocalizedText)
          .join(" ")
          .toLowerCase();

      return searchText.includes(
        searchQuery
          .trim()
          .toLowerCase()
      );
    });

  /*
   * =====================================================
   * GET LOCATION NAMES
   * =====================================================
   */

  const getLocations = (item) => {
    if (
      !Array.isArray(item?.locations)
    ) {
      return [];
    }

    return item.locations
      .map((locationId) => {
        const location =
          locations.find(
            (item) =>
              Number(item.id) ===
              Number(locationId)
          );

        return (
          location?.name ||
          location?.value ||
          String(locationId)
        );
      });
  };

  /*
   * =====================================================
   * GET EMPLOYMENT TYPE
   * =====================================================
   */

  const getEmploymentType = (item) => {
    if (!item?.employmentTypeId) {
      return "";
    }

    const employmentType =
      employmentTypes.find(
        (type) =>
          Number(type.id) ===
          Number(
            item.employmentTypeId
          )
      );

    return (
      employmentType?.name ||
      employmentType?.value ||
      ""
    );
  };

  /*
   * =====================================================
   * GET WORK MODE
   * =====================================================
   */

  const getWorkMode = (item) => {
    if (!item?.workModeId) {
      return "";
    }

    const workMode =
      workModes.find(
        (mode) =>
          Number(mode.id) ===
          Number(
            item.workModeId
          )
      );

    return (
      workMode?.name ||
      workMode?.value ||
      ""
    );
  };

  /*
   * =====================================================
   * EXPERIENCE
   * =====================================================
   */

  const getExperience = (item) => {
    const min =
      item?.experienceMin;

    const max =
      item?.experienceMax;

    if (
      min === undefined ||
      min === null ||
      max === undefined ||
      max === null
    ) {
      return "";
    }

    return `${min}-${max} Yrs`;
  };

  /*
   * =====================================================
   * JD CHECK
   * =====================================================
   *
   * A Role Details record itself represents
   * a configured Job/JD.
   *
   * Therefore we don't need the old hasJD logic
   * for these API records.
   * =====================================================
   */

  const hasJD = (item) => {
    return Boolean(
      item?.overview ||
      item?.responsibilities ||
      item?.requirements ||
      item?.benefits
    );
  };

  /*
   * =====================================================
   * TOTAL PAGES
   * =====================================================
   */

  const totalPages =
    pagination?.totalPages || 1;

  /*
   * =====================================================
   * UI
   * =====================================================
   */

  const handleDeleteRoleDetail = async (itemId) => {
    if (!itemId) {
      message.error("Role detail ID is missing.");
      return;
    }

    try {
      setLoading(true);

      await deleteRoleDetail(itemId, {
        adminClientId: Number(accountId),
      });

      message.success(
        "Job Details Deleted Successfully"
      );

      const nextPage =
        roleDetails.length === 1 &&
          currentPage > 1
          ? currentPage - 1
          : currentPage;

      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await fetchRoleDetails(currentPage);
      }
    } catch (error) {
      console.error(
        "Error deleting role detail:",
        error
      );

      message.error(
        error?.response?.data?.message ||
        "Failed to delete job details."
      );
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (itemId, jobName) => {
    setSelectedDeleteId(itemId);
    setDeleteModalOpen(true);
    setSelectedDeleteJobName(jobName);
  };

  const closeDeleteModal = () => {
    setSelectedDeleteId(null);
    setDeleteModalOpen(false);
    setSelectedDeleteJobName("");
  };
  return (
    <div className="min-h-screen bg-[#F6F6F6]">

      {/* =================================================
          HEADER
      ================================================= */}

      <ReusableHeader />

      <div className="px-6 lg:px-[45px] pb-[40px]">

        {/* =================================================
            TOP SECTION
        ================================================= */}

        <div
          className="
            flex
            justify-between
            items-start
            mt-[12px]
          "
        >

          {/* LEFT SIDE */}

        <div className="relative top-[10px]">

            {/* BACK BUTTON */}

            <button
              onClick={() =>
                navigate(
                  "/sync-configuration",
                  {
                    state: fullState,
                  }
                )
              }
              className="
                flex
                items-center
                gap-2
                bg-[#C21A17]
                text-white
                px-5
                py-2
                rounded-lg
                text-sm
                font-semibold
                shadow-md
                hover:bg-[#A91513]
                transition-all
                duration-200
                -mt-[20px]
              "
            >
              <img
                src={BackIcon}
                alt="Back"
                className="
                  w-[15px]
                  h-[15px]
                  object-contain
                "
              />

              Back
            </button>

            {/* BREADCRUMB */}

            <div
              className="
                mt-[18px]
                ml-[3px]
                flex
                items-center
                text-[14px]
                leading-[18px]
                text-[#939393]
              "
            >

            <div className="relative -top-[6px] flex items-center text-[14px] text-[#939393]">
                {/* Account Name */}
                <span
                  onClick={() => navigate("/admin")}
                  className="cursor-pointer"
                >
                  {accountName}
                </span>

                <span className="mx-[5px]">
                  ›
                </span>

                {/* Role Name */}
                <span
                  onClick={() =>
                    navigate("/dashboard", {
                      state: fullState,
                    })
                  }
                  className="cursor-pointer"
                >
                  {fullState?.name || ""}
                </span>

                <span className="mx-[5px]">
                  ›
                </span>

                {/* Experience Configuration */}
                <span
                  onClick={() =>
                    navigate("/experience-configuration", {
                      state: fullState,
                    })
                  }
                  className="cursor-pointer"
                >
                  Experience Configuration
                </span>

                <span className="mx-[5px]">
                  ›
                </span>

                {/* Product Configuration */}
                <span
                  onClick={() =>
                    navigate("/productsConfiguration", {
                      state: fullState,
                    })
                  }
                  className="cursor-pointer"
                >
                  Product Configuration
                </span>

                <span className="mx-[5px]">
                  ›
                </span>

                {/* Sync */}
                <span
                  onClick={() =>
                    navigate("/sync-configuration", {
                      state: fullState,
                    })
                  }
                  className="cursor-pointer"
                >
                  Sync
                </span>

                <span className="mx-[5px]">
                  ›
                </span>

                {/* Current Page */}
                <span className="font-semibold text-[#1B1B1B]">
                  Role details Configuration
                </span>
              </div>

            </div>
          </div>

          {/* HOME BUTTON */}

          <button
            onClick={() =>
              navigate("/admin")
            }
            className="
              flex
              items-center
              gap-2
              bg-[#C21A17]
              text-white
              px-5
              py-2
              rounded-lg
              text-sm
              font-semibold
              shadow-md
              hover:bg-[#A91513]
              transition-all
              duration-200
              -mt-[10px]
            "
          >
            <img
              src={HomeIcon}
              alt="Home"
              className="
                w-[15px]
                h-[15px]
                object-contain
              "
            />

            Home
          </button>

        </div>

        {/* =================================================
            MAIN CARD
        ================================================= */}

        <div
          className="
            mt-8
            w-full
            bg-white
            rounded-xl
            p-4
            shadow-sm
          "
        >

          {/* TITLE */}

          <h2
            className="
              m-0
              text-[18px]
              leading-[22px]
              font-semibold
              text-[#111111]
            "
          >
            Role details Configuration
          </h2>

          {/* =================================================
              SEARCH + ADD
          ================================================= */}

          <div
            className="
              flex
              items-center
              gap-3
              mt-4
              w-full
            "
          >

            {/* SEARCH */}

            <div
              className="
                relative
                flex-1
              "
            >

              <input
                type="text"
                placeholder="Search Role..."
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(
                    event.target.value
                  );

                  setCurrentPage(1);
                }}
                className="
                  w-full
                  h-10
                  border
                  border-[#C9C9C9]
                  rounded-md
                  bg-white
                  pl-[16px]
                  pr-[45px]
                  outline-none
                  text-sm
                  text-[#333333]
                  placeholder:text-[#A0A0A0]
                  focus:border-[#B8B8B8]
                "
              />

              <Search
                size={20}
                strokeWidth={2}
                className="
                  absolute
                  right-[16px]
                  top-1/2
                  -translate-y-1/2
                  text-[#111111]
                  pointer-events-none
                "
              />

            </div>

            {/* ADD ROLE & JD */}

            <button
              type="button"
              onClick={() =>
                navigate(
                  "/add-job-details",
                  {
                    state: {
                      ...fullState,
                      editMode: false,
                      prefillJobName: "",
                      prefillDesignation: "",
                    },
                  }
                )
              }
              className="
                shrink-0
                h-10
                min-w-[150px]
                px-5
                rounded-md
                bg-[#C90000]
                text-white
                text-sm
                font-medium
                border-none
                cursor-pointer
                hover:bg-[#AE0000]
                transition-colors
                duration-200
              "
            >
              Add Role & JD
            </button>

          </div>

          {/* =================================================
              ROLE DATA
          ================================================= */}

          {loading ? (

            <div
              className="
                flex
                justify-center
                items-center
                h-[125px]
              "
            >
              <Spin size="large" />
            </div>

          ) : filteredRoleDetails.length ===
            0 ? (

            <div
              className="
                h-[125px]
                flex
                items-center
                justify-center
                text-[#B5B5B5]
                text-[15px]
              "
            >
              {searchQuery
                ? "No roles found"
                : "No Data Available"}
            </div>

          ) : (

            <div className="mt-4">

              {filteredRoleDetails.map(
                (item, index) => {

                  const itemId =
                    item?._id ||
                    item?.id;

                  const designation =
                    getLocalizedText(
                      item?.designation ||
                      ""
                    );

                  /*
                   * For this API:
                   *
                   * roleName = parent Role
                   * companyName = Client
                   * designation = Job
                   *
                   * The card's main title should
                   * therefore be the designation.
                   */

                  const jobName =
                    designation ||
                    getLocalizedText(
                      item?.roleName ||
                      ""
                    );

                  const locationNames =
                    getLocations(item);

                  const employmentType =
                    getEmploymentType(item);

                  const workMode =
                    getWorkMode(item);

                  const experience =
                    getExperience(item);

                  const jdAdded =
                    hasJD(item);

                  const isActive =
                    item?.isActive !== false;

                  return (
                    <div
                      key={
                        itemId ||
                        `role-${index}`
                      }
                      className="
                        w-full
                        min-h-[96px]
                        border
                        border-[#D0D0D0]
                        rounded-xl
                        bg-white
                        flex
                        items-center
                        justify-between
                        gap-3
                        flex-wrap
                        px-4
                        py-3
                        mb-3
                      "
                    >

                      {/* =================================================
                          LEFT SIDE
                      ================================================= */}

                      <div
                        className="
                          flex
                          items-center
                          gap-[12px]
                          flex-1
                          min-w-0
                        "
                      >

                        {/* AVATAR */}

                        {item?.jobLogo ? (

                          <img
                            src={item.jobLogo}
                            alt={jobName}
                            className="
                              w-16
                              h-16
                              rounded-full
                              object-cover
                              shrink-0
                            "
                          />

                        ) : (

                          <div
                            className="
                              w-16
                              h-16
                              rounded-full
                              bg-[#2F5D8C]
                              flex
                              items-center
                              justify-center
                              text-white
                              text-[28px]
                              font-semibold
                              shrink-0
                            "
                          >
                            {(jobName ||
                              "R")
                              .charAt(0)
                              .toUpperCase()}
                          </div>

                        )}

                        {/* JOB INFORMATION */}

                        <div className="min-w-0">

                          <h3
                            className="
                              m-0
                              text-[16px]
                              leading-[20px]
                              font-semibold
                              text-[#111111]
                            "
                          >
                            {jobName}
                          </h3>

                          <p
                            className="
                              m-0
                              mt-[5px]
                              text-[14px]
                              leading-[18px]
                              text-[#8A8A8A]
                            "
                          >
                            {item?.roleName ||
                              ""}
                          </p>

                          {/* TAGS */}

                          {jdAdded && (

                            <div
                              className="
                                flex
                                items-center
                                flex-wrap
                                gap-[9px]
                                mt-[7px]
                              "
                            >

                              {/* LOCATIONS */}

                              {locationNames.length >
                                0 && (

                                  <span
                                    className="
                                    px-[10px]
                                    py-[4px]
                                    rounded-full
                                    bg-[#EEEEEE]
                                    text-[#777777]
                                    text-[12px]
                                    leading-[14px]
                                    font-medium
                                  "
                                  >
                                    {locationNames[0]}

                                    {locationNames.length >
                                      1 &&
                                      ` +${locationNames.length -
                                      1
                                      }`}
                                  </span>

                                )}

                              {/* EMPLOYMENT TYPE */}

                              {employmentType && (

                                <span
                                  className="
                                    px-[10px]
                                    py-[4px]
                                    rounded-full
                                    bg-[#EEEEEE]
                                    text-[#777777]
                                    text-[12px]
                                    leading-[14px]
                                    font-medium
                                  "
                                >
                                  {employmentType}
                                </span>

                              )}

                              {/* EXPERIENCE */}

                              {experience && (

                                <span
                                  className="
                                    px-[10px]
                                    py-[4px]
                                    rounded-full
                                    bg-[#EEEEEE]
                                    text-[#777777]
                                    text-[12px]
                                    leading-[14px]
                                    font-medium
                                  "
                                >
                                  {experience}
                                </span>

                              )}

                              {/* WORK MODE */}

                              {workMode && (

                                <span
                                  className="
                                    px-[10px]
                                    py-[4px]
                                    rounded-full
                                    bg-[#EEEEEE]
                                    text-[#777777]
                                    text-[12px]
                                    leading-[14px]
                                    font-medium
                                  "
                                >
                                  {workMode}
                                </span>

                              )}

                            </div>

                          )}

                        </div>

                      </div>

                      {/* =================================================
                          RIGHT SIDE
                      ================================================= */}

                      <div
                        className="
                          flex
                          flex-col
                          items-end
                          justify-between
                          self-stretch
                          min-w-[105px]
                        "
                      >

                        {/* ACTIVE */}

                        <span
                          className={`
                            px-[11px]
                            py-[4px]
                            rounded-[6px]
                            text-[12px]
                            leading-[16px]
                            font-semibold
                            ${isActive
                              ? "bg-[#E8FBE8] text-[#149329]"
                              : "bg-[#FDEAEA] text-[#D22A2A]"
                            }
                          `}
                        >
                          {isActive
                            ? "Active"
                            : "Inactive"}
                        </span>

                        {/* DELETE + EDIT */}

                        <div
                          className="
                            flex
                            items-center
                            gap-2
                          "
                        >

                          {/* EDIT */}

                          <button
                            type="button"
                            aria-label={`Edit ${jobName}`}
                            onClick={() =>
                              navigate(
                                "/add-job-details",
                                {
                                  state: {
                                    ...fullState,
                                    jdId:
                                      itemId,
                                    editMode: true,
                                    prefillJobName:
                                      jobName,
                                    prefillDesignation:
                                      designation,
                                  },
                                }
                              )
                            }
                            className="
                              w-8
                              h-8
                              rounded-full
                              bg-[#F2F4F7]
                              border-none
                              flex
                              items-center
                              justify-center
                              cursor-pointer
                            "
                          >
                            <img
                              src={editIcon}
                              alt=""
                              className="
                                w-[15px]
                                h-[15px]
                                object-contain
                              "
                            />
                          </button>

                          {/* DELETE */}

                          <button
                            type="button"
                            aria-label={`Delete ${jobName}`}
                            onClick={() => openDeleteModal(itemId, jobName)}
                            className="
                              w-8
                              h-8
                              rounded-full
                              bg-[#F2F4F7]
                              border-none
                              flex
                              items-center
                              justify-center
                              cursor-pointer
                            "
                          >
                            <img
                              src={DeleteIcon}
                              alt=""
                              className="
                                w-[28px]
                                h-[28px]
                                object-contain
                              "
                            />
                          </button>

                        </div>

                      </div>

                    </div>
                  );
                }
              )}

            </div>

          )}

          {/* =================================================
              PAGINATION
          ================================================= */}

          <div
            className="
              flex
              justify-end
              items-center
              gap-[14px]
              mt-[18px]
            "
          >

            {/* FIRST */}

            <button
              type="button"
              disabled={
                currentPage === 1
              }
              onClick={() => {
                setCurrentPage(1);
                setSearchQuery("");
              }}
              className="
                border-none
                bg-transparent
                p-0
                flex
                items-center
                justify-center
                cursor-pointer
              "
            >
              <ChevronsLeft
                size={17}
                strokeWidth={2}
                className={
                  currentPage === 1
                    ? "text-[#CFCFCF]"
                    : "text-[#444444]"
                }
              />
            </button>

            {/* PREVIOUS */}

            <button
              type="button"
              disabled={
                currentPage === 1
              }
              onClick={() =>
                setCurrentPage(
                  (previous) =>
                    Math.max(
                      previous - 1,
                      1
                    )
                )
              }
              className="
                border-none
                bg-transparent
                p-0
                flex
                items-center
                justify-center
                cursor-pointer
              "
            >
              <ChevronLeft
                size={17}
                strokeWidth={2}
                className={
                  currentPage === 1
                    ? "text-[#CFCFCF]"
                    : "text-[#444444]"
                }
              />
            </button>

            {/* CURRENT PAGE */}

            <span
              className="
                text-[14px]
                text-[#555555]
                min-w-[25px]
                text-center
              "
            >
              {currentPage}
            </span>

            {/* NEXT */}

            <button
              type="button"
              disabled={
                currentPage >=
                totalPages
              }
              onClick={() =>
                setCurrentPage(
                  (previous) =>
                    Math.min(
                      previous + 1,
                      totalPages
                    )
                )
              }
              className="
                border-none
                bg-transparent
                p-0
                flex
                items-center
                justify-center
                cursor-pointer
              "
            >
              <ChevronRight
                size={17}
                strokeWidth={2}
                className={
                  currentPage >=
                    totalPages
                    ? "text-[#CFCFCF]"
                    : "text-[#444444]"
                }
              />
            </button>

            {/* LAST */}

            <button
              type="button"
              disabled={
                currentPage >=
                totalPages
              }
              onClick={() =>
                setCurrentPage(
                  totalPages
                )
              }
              className="
                border-none
                bg-transparent
                p-0
                flex
                items-center
                justify-center
                cursor-pointer
              "
            >
              <ChevronsRight
                size={17}
                strokeWidth={2}
                className={
                  currentPage >=
                    totalPages
                    ? "text-[#CFCFCF]"
                    : "text-[#444444]"
                }
              />
            </button>

          </div>

        </div>
      </div>
      <Modal
        open={deleteModalOpen}
        onCancel={closeDeleteModal}
        centered
        closeIcon={
          <span className="relative top-[2px]">
            ✕
          </span>
        }
        footer={[
          <button
            key="cancel"
            type="button"
            onClick={closeDeleteModal}
            className="
        px-5
        py-2
        rounded-[6px]
        border
        border-[#D9D9D9]
        bg-white
        text-[#333333]
        text-[14px]
        font-medium
        hover:bg-[#F5F5F5]
      "
          >
            Cancel
          </button>,

          <button
            key="delete"
            type="button"
            onClick={async () => {
              if (!selectedDeleteId) return;

              await handleDeleteRoleDetail(selectedDeleteId);

              closeDeleteModal();
            }}
            className="
        ml-3
        px-5
        py-2
        rounded-[6px]
        border-none
        bg-[#C90000]
        text-white
        text-[14px]
        font-medium
        hover:bg-[#AE0000]
      "
          >
            Delete
          </button>
        ]}
      >
        <div className="py-2">
          <h3 className="text-[18px] font-semibold text-[#222222]">
            Are you sure you want to delete the{" "}
            {selectedDeleteJobName} Job Role?
          </h3>

          <p className="mt-3 text-[16px] leading-[20px] text-[#666666]">
            This will permanently delete the Job Role, This
            action cannot be undone.
          </p>

          <p className="mt-4 text-[16px] text-[#555555]">
            Are you <span className="font-semibold">Sure?</span>
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default RoleDetailsConfiguration;